import { useContext, useEffect, useRef } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { AuthContext } from "react-oauth2-code-pkce";

import { useApp } from "../../app/Application.provider";
import { buildTenantUrl, consumeAuthState } from "../../auth/utils";
import { LOGIN_ROUTE, SAFE_FALLBACK_ROUTE, SUCCESS_ROUTE } from "../../auth/constants";

export const Route = createFileRoute("/auth/callback")({
  component: CallbackPage,
});

// Visual processing state shown while the OAuth code exchange completes.
// Navigated to by routes/index.tsx when it detects an OAuth callback in progress.
// After auth.isAuthenticated flips true (via OAuthBridge), this route resolves
// the final destination from the stored OAuth state and performs the navigation.
function CallbackPage() {
  const { auth } = useApp();
  const { loginInProgress, error: oauthError } = useContext(AuthContext);
  const navigate = useNavigate();
  const hasNavigatedRef = useRef(false);

  useEffect(() => {
    // Still exchanging the code — wait.
    if (loginInProgress) return;
    if (hasNavigatedRef.current) return;

    if (auth.isAuthenticated) {
      hasNavigatedRef.current = true;

      // Consume the stored OAuth state (written by login.tsx before calling logIn()).
      // This is the single place that reads and removes the state for the OAuth flow.
      const state = consumeAuthState();
      const destination = state?.redirect ?? SAFE_FALLBACK_ROUTE;

      if (state?.tenant) {
        // Cross-origin navigation: user authenticated from a tenant subdomain
        // (e.g., acme.loa.localhost) but the callback landed at the root domain.
        // Hard-redirect to restore the tenant context.
        window.location.href = buildTenantUrl(state.tenant, destination);
      } else {
        void navigate({ to: destination as string, replace: true });
      }
      return;
    }

    if (oauthError) {
      hasNavigatedRef.current = true;
      void navigate({
        to: LOGIN_ROUTE as string,
        replace: true,
      });
      return;
    }

    // loginInProgress=false, not authenticated, no error — unexpected state.
    // Redirect to success as a safe fallback so the user isn't stranded.
    if (!loginInProgress && !auth.isAuthenticated && !oauthError) {
      hasNavigatedRef.current = true;
      void navigate({ to: SUCCESS_ROUTE as string, replace: true });
    }
  }, [loginInProgress, auth.isAuthenticated, oauthError, navigate]);

  // Reset ref if the user navigates back here after a logout
  useEffect(() => {
    if (!auth.isAuthenticated && !loginInProgress) {
      hasNavigatedRef.current = false;
    }
  }, [auth.isAuthenticated, loginInProgress]);

  return (
    <div style={{ textAlign: "center", padding: "2rem" }}>
      <div
        style={{
          width: 40,
          height: 40,
          border: "3px solid #f3f3f3",
          borderTop: "3px solid #3498db",
          borderRadius: "50%",
          animation: "spin 1s linear infinite",
          margin: "0 auto 1rem",
        }}
      />
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      <p style={{ color: "#6b7280", fontSize: "0.875rem" }}>Completing sign-in…</p>
    </div>
  );
}
